import IconLink from 'images/icon_link.svg'
import React from 'react'
import { Button, ButtonGroup, Card, Modal, Spinner } from 'react-bootstrap'
import { PositionNumberType } from '../api/common/constants'
import { QrCode } from './QrCode'

export const PositionState = {
  Contracted: 0,
  UnContract: 1,
  ChangedAssignment: 2,
  ChangingAssignment: 3,
  Withdrawal: 4,
  CoolingOff: 5,
}
export type PositionStateType = (typeof PositionState)[keyof typeof PositionState]

/**
 * 紹介QR表示モード (Rev.19 S44)
 * 招待者がポジション詳細画面で「通常QR / キャンペーンQR」をトグル選択する。
 */
export const QrMode = {
  Normal: 'normal',
  Campaign: 'campaign',
} as const
export type QrModeType = (typeof QrMode)[keyof typeof QrMode]
export interface PositionProps {
  name: string
  state: PositionStateType
  connectorId: string
  connectorIntroductionLeftURL: string
  connectorIntroductionRightURL: string
  campaignConnectorIntroductionLeftURL: string
  campaignConnectorIntroductionRightURL: string
  atemQuestId: string
  userIntroductionURL: string
  positionNumber?: PositionNumberType
  onContractConnector?: () => void
  onToast?: () => void
  isSpinningSubmit?: boolean
}

export const PositionListCard: React.FC<PositionProps> = ({
  name,
  state,
  connectorId,
  connectorIntroductionLeftURL,
  connectorIntroductionRightURL,
  campaignConnectorIntroductionLeftURL,
  campaignConnectorIntroductionRightURL,
  atemQuestId,
  userIntroductionURL,
  onContractConnector,
  onToast,
  isSpinningSubmit,
}) => {
  const ModalTarget = {
    ConnectorLeft: 'connectorLeft',
    ConnectorRight: 'connectorRight',
    User: 'user',
  } as const
  type ModalTargetType = (typeof ModalTarget)[keyof typeof ModalTarget]
  const [modalTarget, setModalTarget] = React.useState<ModalTargetType | null>(null)
  const [qrMode, setQrMode] = React.useState<QrModeType>(QrMode.Normal)
  const displayedLeftUrl = qrMode === QrMode.Campaign ? campaignConnectorIntroductionLeftURL : connectorIntroductionLeftURL
  const displayedRightUrl = qrMode === QrMode.Campaign ? campaignConnectorIntroductionRightURL : connectorIntroductionRightURL
  const copyClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    onToast?.()
  }

  const stateItem = (() => {
    switch (state) {
      case PositionState.Contracted:
        return (
          <div>
            <div className="mt-4">
              <div className="fw-bold">コネクターID</div>
              <hr className="my-2" />
              <div>{connectorId}</div>
            </div>
            <div className="mt-5">
              <ButtonGroup className="w-100">
                <Button variant="outline-primary" className={qrMode === QrMode.Normal ? 'active' : ''} onClick={() => setQrMode(QrMode.Normal)}>
                  通常QR
                </Button>
                <Button variant="outline-primary" className={qrMode === QrMode.Campaign ? 'active' : ''} onClick={() => setQrMode(QrMode.Campaign)}>
                  仮登録QR
                </Button>
              </ButtonGroup>
            </div>
            <div className="mt-5">
              <div className="fw-bold">
                コネクター紹介用URL<span className="text-danger">(左系列)</span>
              </div>
              <hr className="my-2" />
              <div>{displayedLeftUrl}</div>
              <div className="mt-4">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => copyClipboard(displayedLeftUrl)}>
                  <img src={IconLink} alt="Link" className="me-1" />
                  リンクをコピーする<span className="text-danger">(左系列)</span>
                </Button>
              </div>
              <div className="mt-3">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => setModalTarget(ModalTarget.ConnectorLeft)}>
                  紹介用QRコードを表示<span className="text-danger">(左系列)</span>
                </Button>
                <Modal size="sm" show={modalTarget === ModalTarget.ConnectorLeft} onHide={() => setModalTarget(null)} aria-labelledby="contained-modal-title-vcenter" centered>
                  <Modal.Body className="text-center">
                    <QrCode targetString={displayedLeftUrl} />
                  </Modal.Body>
                </Modal>
              </div>
            </div>
            <div className="mt-5">
              <div className="fw-bold">
                コネクター紹介用URL<span className="text-danger">(右系列)</span>
              </div>
              <hr className="my-2" />
              <div>{displayedRightUrl}</div>
              <div className="mt-4">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => copyClipboard(displayedRightUrl)}>
                  <img src={IconLink} alt="Link" className="me-1" />
                  リンクをコピーする<span className="text-danger">(右系列)</span>
                </Button>
              </div>
              <div className="mt-3">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => setModalTarget(ModalTarget.ConnectorRight)}>
                  紹介用QRコードを表示<span className="text-danger">(右系列)</span>
                </Button>
                <Modal size="sm" show={modalTarget === ModalTarget.ConnectorRight} onHide={() => setModalTarget(null)} aria-labelledby="contained-modal-title-vcenter" centered>
                  <Modal.Body className="text-center">
                    <QrCode targetString={displayedRightUrl} />
                  </Modal.Body>
                </Modal>
              </div>
            </div>
            <div className="mt-5">
              <div className="fw-bold">AUBEクエストID</div>
              <hr className="my-2" />
              <div>{atemQuestId}</div>
            </div>
            <div className="mt-5">
              <div className="fw-bold">ユーザー紹介用URL</div>
              <hr className="my-2" />
              <div>{userIntroductionURL}</div>
              <div className="mt-4">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => copyClipboard(userIntroductionURL)}>
                  <img src={IconLink} alt="Link" className="me-1" />
                  リンクをコピーする
                </Button>
              </div>
              <div className="mt-3">
                <Button variant="outline-primary" className="w-100 py-4" onClick={() => setModalTarget(ModalTarget.User)}>
                  紹介用QRコードを表示
                </Button>
                <Modal size="sm" show={modalTarget === ModalTarget.User} onHide={() => setModalTarget(null)} aria-labelledby="contained-modal-title-vcenter" centered>
                  <Modal.Body className="text-center">
                    <QrCode targetString={userIntroductionURL} />
                  </Modal.Body>
                </Modal>
              </div>
            </div>
          </div>
        )
      case PositionState.UnContract:
        return (
          <div>
            <div className="mt-4">
              <div className="fw-bold">コネクターID</div>
              <hr className="my-2" />
              <div>{connectorId}</div>
            </div>
            <div className="mt-5">
              <div className="fw-bold">AUBEクエストID</div>
              <hr className="my-2" />
              <div>{atemQuestId || '未設定'}</div>
            </div>
            <Button variant="primary" className="w-100 py-4 mt-4" onClick={() => onContractConnector?.()} disabled={isSpinningSubmit}>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isSpinningSubmit} />
              コネクター登録
            </Button>
          </div>
        )
      case PositionState.ChangedAssignment:
        return <div className="position-message mt-4">名義変更しました</div>
      case PositionState.ChangingAssignment:
        return <div className="position-message mt-4">名義変更依頼中</div>
      case PositionState.Withdrawal:
        return <div className="position-message mt-4">解約しました</div>
      case PositionState.CoolingOff:
        return <div className="position-message mt-4">クーリングオフしました</div>
      default:
        return <div></div>
    }
  })()
  return (
    <Card body className="mt-4 border-0" bg={state === PositionState.ChangedAssignment || state === PositionState.ChangingAssignment ? 'disabled' : ''}>
      <h4 className="position-title">{name}</h4>
      {stateItem}
    </Card>
  )
}
